import { DefaultApolloClient } from '@vue/apollo-composable'
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client/core'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { Notify } from 'quasar'
import { persistCacheSync } from 'apollo3-cache-persist'

import schema from 'src/graphql/graphql.possibleTypes.json'
import { useAuthStore } from 'stores/auth'
import TokenExpiredError from 'src/graphql/errors/TokenExpiredError'
import { defineBoot } from '#q-app/wrappers'
import Router from 'src/router'
import { containsGqlErrorCode } from 'src/helpers/error.helper'
import { IErrorCode } from 'src/graphql'
import { Kind, OperationTypeNode } from 'graphql/language'
import {
  ApolloPersistedStorage,
  isIndexedDBSupported,
} from 'src/utils/apollo/ApolloPersistedStorage'
import { i18n } from 'boot/i18n'

const GRAPHQL_URL = process.env.GRAPHQL_URL
const GRAPHQL_SUBSCRIPTION_URL = process.env.GRAPHQL_SUBSCRIPTION_URL

const cache = new InMemoryCache({
  possibleTypes: schema.possibleTypes,
})

if (isIndexedDBSupported()) {
  persistCacheSync({
    cache,
    storage: new ApolloPersistedStorage(),
    trigger: 'write',
    debounce: 1000, // Debounce writes by 1 second to reduce storage operations
    key: '', // Using an empty string to avoid any prefix
    debug: process.env.NODE_ENV === 'development', // Enable debug logs in development
  })
}

const retryLink = new RetryLink({
  attempts: {
    max: 2,
    retryIf: async (error: Error) => {
      if (error.message === 'TOKEN_EXPIRED') {
        const store = useAuthStore()
        await store.refreshAuthToken()

        return true
      }
      return false
    },
  },
})

const authLink = new ApolloLink((operation, forward) => {
  const store = useAuthStore()

  const token = store.accessToken
  if (token && operation.operationName !== 'RefreshToken') {
    const headers = operation.getContext().headers || {}
    operation.setContext({
      headers: {
        ...headers,
        authorization: 'Bearer ' + token,
      },
    })
  }
  return forward(operation)
})

const wsLink = new GraphQLWsLink(
  createClient({
    url: GRAPHQL_SUBSCRIPTION_URL,
  }),
)

const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
})

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.SUBSCRIPTION
    )
  },
  wsLink,
  httpLink,
)

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    retryLink,
    authLink,
    new ApolloLink((operation, forward) => {
      return forward(operation).map((data) => {
        if (data && data.errors && data.errors.length > 0) {
          const authStore = useAuthStore()
          if (containsGqlErrorCode(data, IErrorCode.UnauthorizedRefreshTokenExpired)) {
            authStore.setAfterSignInRedirection(Router.currentRoute.value.fullPath)

            Notify.create({
              message: i18n.global.t('general.error.refreshTokenExpired'),
              color: 'white',
              textColor: 'dark',
              icon: 'fa-regular fa-user-shield',
              timeout: 3000,
              iconSize: 'lg',
              iconColor: 'dark',
            })

            return authStore.signOut()
          }
          if (containsGqlErrorCode(data, IErrorCode.UnauthorizedTokenExpired)) {
            throw new TokenExpiredError()
          }
          if (
            containsGqlErrorCode(data, IErrorCode.Unauthorized) ||
            containsGqlErrorCode(data, IErrorCode.UnauthorizedTokenInvalid)
          ) {
            void authStore.signOut()
          }
        }
        return data
      })
    }),
    splitLink,
  ]),
  cache,
})

export default defineBoot(({ app }) => {
  app.provide(DefaultApolloClient, apolloClient)
})
